import { Select } from '@HoudiniUI/Select';
import { MultipleSelect } from '@WebComponents/MultipleSelect';
import { Toggle } from '@WebComponents/Toggle';
import { css, StyleDeclaration } from 'aphrodite';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { StandardButtonStyles } from '../../../../../../../web/components-v2/Button/styles';
import {
	Combobox,
	ComboboxChip,
	ComboboxChipRemove,
	ComboboxChips,
	ComboboxClear,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItemCheckboxIndicator,
	ComboboxItem as ComboboxItemComponent,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxTrigger,
	ComboboxValue,
} from '../../../../../../../web/components-v2/Combobox';
import { SimpleAutoCompleteSearchField } from '../../../../../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { Button } from '../../../../../../../web/components/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../../../web/components/Popover';
import { bs } from '../../../../../../../web/styles/styles';
import type { ComboboxItem } from './providers/types';
import { styleSheet } from './styles';

export type { ComboboxItem };

interface CustomStylesProps {
	customStyles?: StyleDeclaration[];
	customInlineStyles?: React.CSSProperties;
}

interface EditModeContextType {
	isEditing: boolean;
}

const EditModeContext = createContext<EditModeContextType | null>(null);

export const EditModeProvider: React.FC<{
	isEditing: boolean;
	children: React.ReactNode;
}> = ({ isEditing, children }) => {
	return <EditModeContext.Provider value={{ isEditing }}>{children}</EditModeContext.Provider>;
};

export const useEditMode = (): EditModeContextType => {
	const context = useContext(EditModeContext);
	if (!context) {
		throw new Error('useEditMode must be used within an EditModeProvider');
	}
	return context;
};

export const EditModeMultipleSelect = (
	props: React.ComponentProps<typeof MultipleSelect> & { displayValue: string } & CustomStylesProps
) => {
	const { isEditing } = useEditMode();
	const containerRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout>;
		// Check if text is overflowing
		const checkOverflow = () => {
			const element = containerRef.current;
			if (element) {
				// Use setTimeout to ensure DOM is fully rendered
				timeoutId = setTimeout(() => {
					const isOverflow = element.scrollWidth > element.clientWidth || element.offsetWidth < element.scrollWidth;
					setIsOverflowing(isOverflow);
				}, 0);
			}
		};

		checkOverflow();
		// Also check on window resize
		window.addEventListener('resize', checkOverflow);
		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', checkOverflow);
		};
	}, [props.displayValue]);

	if (!isEditing) {
		// Display toggle value as text when not in edit mode
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;

		return (
			<div
				ref={containerRef}
				className={css(
					styleSheet.readOnlyField,
					isOverflowing && styleSheet.readOnlyFieldOverflow,
					...propsCustomStyles
				)}
				style={propsInlineStyles}
				title={isOverflowing ? props.displayValue : undefined}
			>
				{props.displayValue}
			</div>
		);
	}

	const { displayValue, customStyles, customInlineStyles, ...multiSelectProps } = props;
	return <MultipleSelect {...multiSelectProps} />;
};

export const EditModeToggle = (
	props: React.ComponentProps<typeof Toggle> & { displayValue?: string } & CustomStylesProps
) => {
	const { isEditing } = useEditMode();

	if (!isEditing) {
		// Display toggle value as text when not in edit mode
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;
		return (
			<div className={css(styleSheet.readOnlyField, ...propsCustomStyles)} style={propsInlineStyles}>
				{props.displayValue ? props.displayValue : props.isOn ? 'Enabled' : 'Disabled'}
			</div>
		);
	}

	const { customStyles, customInlineStyles, ...toggleProps } = props;
	return <Toggle {...toggleProps} />;
};

export function EditModeSelect<Value>(
	props: React.ComponentProps<typeof Select<Value>> & { displayValue: string } & CustomStylesProps
) {
	const { isEditing } = useEditMode();

	if (!isEditing) {
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;
		return (
			<div className={css(styleSheet.readOnlyField, ...propsCustomStyles)} style={propsInlineStyles}>
				{props.displayValue}
			</div>
		);
	}

	const { displayValue, customStyles, customInlineStyles, ...selectProps } = props;
	return <Select<Value> {...selectProps} />;
}

export const EditModeSimpleAutoCompleteSearchField = (
	props: React.ComponentProps<typeof SimpleAutoCompleteSearchField> & {
		displayValue: string;
		textAlign?: React.CSSProperties['textAlign'];
	} & CustomStylesProps
) => {
	const { isEditing } = useEditMode();

	if (!isEditing) {
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;

		return (
			<div className={css(styleSheet.readOnlyField, ...propsCustomStyles)} style={propsInlineStyles}>
				{props.displayValue}
			</div>
		);
	}

	const { displayValue, customStyles, customInlineStyles, textAlign, ...searchFieldProps } = props;
	return <SimpleAutoCompleteSearchField {...searchFieldProps} />;
};

export const EditModeButton = (
	props: React.ComponentProps<typeof Button> & { displayValue: string } & CustomStylesProps
) => {
	const { isEditing } = useEditMode();
	const { customStyles, customInlineStyles, displayValue, ...buttonProps } = props;

	return (
		<Button disabled={!isEditing} {...buttonProps}>
			{displayValue}
		</Button>
	);
};

export const EditModeNumberInput = (
	props: {
		value: number;
		onChange: (value: number) => void;
		displayValue: string;
		min?: number;
		max?: number;
		placeholder?: string;
	} & CustomStylesProps
) => {
	const { isEditing } = useEditMode();

	if (!isEditing) {
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;
		return (
			<div className={css(styleSheet.readOnlyField, ...propsCustomStyles)} style={propsInlineStyles}>
				{props.displayValue}
			</div>
		);
	}

	const { value, onChange, min, max, placeholder, customStyles = [], customInlineStyles } = props;
	return (
		<input
			type='number'
			value={value}
			onChange={e => onChange(Number(e.target.value))}
			min={min}
			max={max}
			placeholder={placeholder}
			className={css(styleSheet.editableField, styleSheet.numberInput, ...customStyles)}
			style={customInlineStyles}
		/>
	);
};

interface EditModeComboboxBaseProps extends CustomStylesProps {
	items: ComboboxItem[];
	displayValue: string;
	searchPlaceholder?: string;
}

interface EditModeComboboxSingleProps extends EditModeComboboxBaseProps {
	multiple?: false;
	value: string;
	onChange: (value: string) => void;
}

interface EditModeComboboxMultipleProps extends EditModeComboboxBaseProps {
	multiple: true;
	value: string[];
	onChange: (value: string[]) => void;
}

type EditModeComboboxProps = EditModeComboboxMultipleProps | EditModeComboboxSingleProps;

export const EditModeCombobox: React.FC<EditModeComboboxProps> = props => {
	const { isEditing } = useEditMode();

	const isMultiple = props.multiple === true;

	if (!isEditing) {
		const { customStyles: propsCustomStyles = [], customInlineStyles: propsInlineStyles } = props;

		return (
			<div
				className={css(styleSheet.readOnlyField, ...propsCustomStyles)}
				style={propsInlineStyles}
				title={props.displayValue}
			>
				{props.displayValue}
			</div>
		);
	}

	const { items, searchPlaceholder = 'Search...' } = props;

	if (isMultiple) {
		const { value, onChange } = props as EditModeComboboxMultipleProps;
		const selectedItems = value.map(id => items.find(item => item.id === id)).filter(Boolean) as ComboboxItem[];

		const handleValueChange = (newValue: ComboboxItem[]) => {
			onChange(newValue.map(item => item.id));
		};

		return (
			<div className={css(styleSheet.editableField)}>
				<Combobox
					multiple
					items={items}
					value={selectedItems}
					onValueChange={newValue => handleValueChange(newValue as ComboboxItem[])}
					isItemEqualToValue={(item, val) => (item as ComboboxItem).id === (val as ComboboxItem).id}
					itemToStringValue={item => (item as ComboboxItem).id}
					itemToStringLabel={item => (item as ComboboxItem).label}
				>
					<ComboboxInput placeholder={searchPlaceholder} showClear />
					<ComboboxChips styles={[styleSheet.comboboxChipsNoWrap]}>
						<ComboboxValue>
							{(selected: ComboboxItem[]) => {
								const visibleCount = 2;
								const visibleItems = selected.slice(0, visibleCount);
								const remainingItems = selected.slice(visibleCount);
								const remainingCount = remainingItems.length;

								return (
									<>
										{visibleItems.map(item => (
											<ComboboxChip key={item.id} aria-label={item.label}>
												{item.label}
												<ComboboxChipRemove />
											</ComboboxChip>
										))}
										{remainingCount > 0 && (
											<Popover>
												<PopoverTrigger asChild>
													<button type='button' className={css(styleSheet.moreChipsLink)}>
														+{remainingCount} more
													</button>
												</PopoverTrigger>
												<PopoverContent styles={[styleSheet.moreChipsPopover]}>
													{remainingItems.map(item => (
														<ComboboxChip key={item.id} aria-label={item.label}>
															{item.label}
															<ComboboxChipRemove />
														</ComboboxChip>
													))}
												</PopoverContent>
											</Popover>
										)}
									</>
								);
							}}
						</ComboboxValue>
					</ComboboxChips>
					<ComboboxContent>
						<ComboboxEmpty>No results found.</ComboboxEmpty>
						<ComboboxList styles={[styleSheet.comboboxList]}>
							{(item: ComboboxItem) => (
								<ComboboxItemComponent key={item.id} value={item}>
									<ComboboxItemCheckboxIndicator />
									{item.label}
								</ComboboxItemComponent>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
			</div>
		);
	}

	// Single-select mode
	const { value: singleValue, onChange: onSingleChange } = props as EditModeComboboxSingleProps;
	const selectedItem = singleValue ? (items.find(item => item.id === singleValue) ?? null) : null;

	const handleSingleValueChange = (newValue: ComboboxItem | null) => {
		onSingleChange(newValue?.id ?? '');
	};

	return (
		<div className={css(styleSheet.editableField)}>
			<Combobox
				items={items}
				value={selectedItem}
				onValueChange={newValue => handleSingleValueChange(newValue as ComboboxItem | null)}
				isItemEqualToValue={(item, val) => (item as ComboboxItem).id === (val as ComboboxItem).id}
				itemToStringValue={item => (item as ComboboxItem).id}
				itemToStringLabel={item => (item as ComboboxItem).label}
			>
				<div className={css(styleSheet.comboboxTriggerWrapper)}>
					<ComboboxTrigger styles={[StandardButtonStyles.selectButton, bs.wFull, bs.py1]}>
						<div className={css(styleSheet.comboboxTrigger)}>
							<ComboboxValue>
								{(value: ComboboxItem | null) => <>{value ? value.label : searchPlaceholder}</>}
							</ComboboxValue>
							<ComboboxClear />
						</div>
					</ComboboxTrigger>
				</div>
				<ComboboxContent styles={[styleSheet.comboboxContentWithInput]}>
					<div className={css(styleSheet.comboboxInputWrapper)}>
						<ComboboxInput placeholder={searchPlaceholder} showTrigger={false} />
					</div>
					<ComboboxEmpty>No results found.</ComboboxEmpty>
					<ComboboxList styles={[styleSheet.comboboxList]}>
						{(item: ComboboxItem) => (
							<ComboboxItemComponent key={item.id} value={item}>
								<ComboboxItemRadioIndicator />
								{item.label}
							</ComboboxItemComponent>
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
		</div>
	);
};
